import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// 教练模式 - 赛事英雄数据钩子（多源 + 合并版本）
//
// 启动流程：
//   1) fetch '/data/index.json' 拿 manifest（数据源清单）
//   2) 用 manifest.defaultSourceIds（数组）勾选默认源
//   3) 同时并发 fetch 所有勾选的 source.file
//   4) 把多份数据按英雄名 picks/bans/wins/losses 累加，重算 PR/BR/WR
//   5) 用户改 selectedSourceIds 后，自动重新拉 + 重新合并
//
// 兼容降级：
//   - 找不到 manifest → 回退到老的单文件 '/tournament_data.json'
//   - 任何一份源加载失败 → 把其它源合并继续展示，错误暂存在 sourceErrors
//
// 暴露给 UI：
//   - sources / selectedSourceIds / setSelectedSourceIds / toggleSource
//   - currentMeta（合并后的伪 meta，含 mergedLabel / totalGames）
//   - data / loading / available
//   - lookup / isOP / winRateTier

const MANIFEST_PATH = '/data/index.json';
const LEGACY_PATH = '/tournament_data.json';
const STORAGE_KEY = 'lolcoach.dataSources.v2';

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const ID_ALIASES = {
  monkeyking: ['wukong'],
  nunu:       ['nunuwillump', 'nunuandwillump'],
  renata:     ['renataglasc'],
};

const LEGACY_FALLBACK_MANIFEST = {
  schemaVersion: 0,
  defaultSourceIds: ['legacy'],
  sources: [
    {
      id: 'legacy',
      label: '默认数据 (单文件)',
      shortLabel: 'default',
      tag: 'regular',
      file: LEGACY_PATH,
    },
  ],
};

// ===== 合并核心 =====
// 把 N 份数据合并成一份"虚拟的"数据，对外行为跟单源完全一致。
// 同英雄的 picks/bans/wins/losses 累加，re-derive win_rate/pick_rate/ban_rate。
function mergeSources(loadedSources) {
  // loadedSources: Array<{ meta, json }>
  const validLoads = loadedSources.filter((x) => x && x.json && Array.isArray(x.json.champions));
  if (validLoads.length === 0) return null;

  // 单份直接返回原始
  if (validLoads.length === 1) {
    return validLoads[0].json;
  }

  const totalGames = validLoads.reduce(
    (sum, x) => sum + (x.json.estimatedTotalGames || 0),
    0,
  );

  // 按 normalize(champion名) 聚合
  const acc = new Map();
  validLoads.forEach(({ json }) => {
    json.champions.forEach((c) => {
      const key = norm(c.champion);
      if (!key) return;
      let cur = acc.get(key);
      if (!cur) {
        cur = {
          champion: c.champion,
          picks: 0, bans: 0, wins: 0, losses: 0,
          // 加权重算用：sum(win_rate * games)/sum(games)
          _wrWeightedSum: 0, _wrWeightedDen: 0,
          _kdaWeightedSum: 0, _kdaWeightedDen: 0,
        };
        acc.set(key, cur);
      }
      cur.picks  += c.picks || 0;
      cur.bans   += c.bans  || 0;
      cur.wins   += c.wins  || 0;
      cur.losses += c.losses || 0;
      const g = c.picks || 0;
      if (g > 0) {
        if (c.win_rate_pct != null) {
          cur._wrWeightedSum += c.win_rate_pct * g;
          cur._wrWeightedDen += g;
        }
        if (c.kda != null) {
          cur._kdaWeightedSum += c.kda * g;
          cur._kdaWeightedDen += g;
        }
      }
    });
  });

  const champions = [];
  acc.forEach((cur) => {
    const games = cur.picks;
    const winRate = cur._wrWeightedDen > 0
      ? Math.round((cur._wrWeightedSum / cur._wrWeightedDen) * 100) / 100
      : (cur.wins + cur.losses > 0
          ? Math.round(cur.wins / (cur.wins + cur.losses) * 10000) / 100
          : null);
    const kda = cur._kdaWeightedDen > 0
      ? Math.round((cur._kdaWeightedSum / cur._kdaWeightedDen) * 100) / 100
      : null;
    const pickRate = totalGames > 0 ? Math.round(cur.picks / totalGames * 10000) / 100 : null;
    const banRate  = totalGames > 0 ? Math.round(cur.bans  / totalGames * 10000) / 100 : null;
    champions.push({
      champion: cur.champion,
      games,
      picks: cur.picks,
      bans: cur.bans,
      wins: cur.wins,
      losses: cur.losses,
      win_rate_pct: winRate,
      pick_rate_pct: pickRate,
      ban_rate_pct:  banRate,
      presence_pct:  pickRate != null && banRate != null
        ? Math.round((pickRate + banRate) * 100) / 100 : null,
      kda,
    });
  });

  // 排序：picks 多到少
  champions.sort((a, b) => (b.picks || 0) - (a.picks || 0));

  // 合并标签：用前 3 个源的 shortLabel 拼，超过 3 个加省略
  const labels = validLoads.map((x) => x.meta.shortLabel || x.meta.label);
  const tournament = labels.length <= 3
    ? labels.join(' + ')
    : `${labels.slice(0, 3).join(' + ')} +${labels.length - 3}`;

  return {
    schemaVersion: 1,
    fetchedAt: new Date().toISOString(),
    source: {
      site: 'merged',
      tournament,
      mergedCount: validLoads.length,
      mergedSources: validLoads.map((x) => x.meta.id),
      season: '混合',
      split: '合并',
    },
    estimatedTotalGames: totalGames,
    champions,
  };
}

// 读 localStorage 里的上次选择（数组）
function loadSavedSelection() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
}

const useTournamentData = () => {
  const [manifest, setManifest] = useState(null);
  const [manifestError, setManifestError] = useState(null);

  // 多选数组
  const [selectedSourceIds, _setSelectedSourceIds] = useState([]);

  // 多源加载的原始数据 + 合并结果
  const [merged, setMerged] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sourceErrors, setSourceErrors] = useState({}); // { sourceId: errMsg }

  // 防止过期请求覆盖
  const reqIdRef = useRef(0);

  // ---------- 1) 加载 manifest ----------
  useEffect(() => {
    let cancelled = false;

    fetch(MANIFEST_PATH, { cache: 'no-cache' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        if (!json || !Array.isArray(json.sources) || json.sources.length === 0) {
          throw new Error('manifest 中没有任何 sources');
        }
        setManifest(json);

        // 决定默认勾选：优先 localStorage > manifest.defaultSourceIds > manifest.defaultSourceId > 第一个
        const saved = loadSavedSelection();
        let initial = null;
        if (saved && saved.length > 0) {
          // 过滤掉 manifest 里已经不存在的 id
          const validIds = new Set(json.sources.map((s) => s.id));
          initial = saved.filter((id) => validIds.has(id));
        }
        if (!initial || initial.length === 0) {
          if (Array.isArray(json.defaultSourceIds) && json.defaultSourceIds.length > 0) {
            initial = json.defaultSourceIds.slice();
          } else if (json.defaultSourceId) {
            initial = [json.defaultSourceId];
          } else {
            initial = [json.sources[0].id];
          }
        }
        _setSelectedSourceIds(initial);
      })
      .catch((e) => {
        if (cancelled) return;
        setManifestError(e.message || String(e));
        setManifest(LEGACY_FALLBACK_MANIFEST);
        _setSelectedSourceIds(LEGACY_FALLBACK_MANIFEST.defaultSourceIds.slice());
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- 2) selectedSourceIds 变化 → 并发拉所有源 → 合并 ----------
  useEffect(() => {
    if (!manifest || selectedSourceIds.length === 0) {
      setMerged(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setSourceErrors({});
    const myReqId = ++reqIdRef.current;

    const metas = selectedSourceIds
      .map((id) => manifest.sources.find((s) => s.id === id))
      .filter(Boolean);

    Promise.all(metas.map((meta) =>
      fetch(meta.file, { cache: 'no-cache' })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((json) => ({ meta, json, error: null }))
        .catch((e) => ({ meta, json: null, error: e.message || String(e) })),
    )).then((results) => {
      if (myReqId !== reqIdRef.current) return;
      const errs = {};
      results.forEach((r) => {
        if (r.error) errs[r.meta.id] = r.error;
      });
      setSourceErrors(errs);
      setMerged(mergeSources(results));
      setLoading(false);
    });
  }, [manifest, selectedSourceIds]);

  // ---------- 3) 持久化用户选择 ----------
  useEffect(() => {
    if (selectedSourceIds.length === 0) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedSourceIds));
    } catch (e) {
      // ignore (隐私模式/配额)
    }
  }, [selectedSourceIds]);

  // ---------- 4) 索引 ----------
  const statByKey = useMemo(() => {
    const m = new Map();
    if (merged && Array.isArray(merged.champions)) {
      merged.champions.forEach((c) => {
        const key = norm(c.champion);
        if (key) m.set(key, c);
      });
    }
    return m;
  }, [merged]);

  const lookup = useMemo(() => {
    return (champion) => {
      if (!champion || statByKey.size === 0) return null;
      const k1 = norm(champion.idName);
      if (statByKey.has(k1)) return statByKey.get(k1);
      const aliases = ID_ALIASES[k1];
      if (aliases) {
        for (const a of aliases) {
          if (statByKey.has(a)) return statByKey.get(a);
        }
      }
      const k2 = norm(champion.name);
      if (k2 && k2 !== k1 && statByKey.has(k2)) return statByKey.get(k2);
      return null;
    };
  }, [statByKey]);

  // ---------- 5) OP / 胜率分档 ----------
  const isOP = useCallback((stat) => {
    if (!stat) return false;
    const pr = stat.pick_rate_pct || 0;
    const br = stat.ban_rate_pct || 0;
    return pr + br >= 80;
  }, []);

  const winRateTier = useCallback((stat) => {
    if (!stat) return null;
    const games = stat.games || stat.picks || 0;
    if (games < 10) return null;
    const wr = stat.win_rate_pct;
    if (wr == null) return null;
    if (wr >= 55) return 'high';
    if (wr <= 45) return 'low';
    return null;
  }, []);

  // ---------- 6) 公开 setter & 切换 ----------
  const setSelectedSourceIds = useCallback((ids) => {
    _setSelectedSourceIds(
      Array.isArray(ids) ? ids.filter(Boolean) : (ids ? [ids] : [])
    );
  }, []);

  const toggleSource = useCallback((id) => {
    _setSelectedSourceIds((prev) => {
      if (prev.includes(id)) {
        // 不允许取消到 0 个 —— 否则页面没数据可看
        if (prev.length === 1) return prev;
        return prev.filter((x) => x !== id);
      }
      return [...prev, id];
    });
  }, []);

  const selectOnly = useCallback((id) => {
    _setSelectedSourceIds([id]);
  }, []);

  const selectByLeague = useCallback((league) => {
    if (!manifest) return;
    const ids = manifest.sources
      .filter((s) => s.league === league)
      .map((s) => s.id);
    if (ids.length > 0) _setSelectedSourceIds(ids);
  }, [manifest]);

  // ---------- 7) 暴露 ----------
  const sources = (manifest && manifest.sources) || [];
  const currentMeta = merged && merged.source
    ? {
        label: merged.source.tournament,
        mergedCount: merged.source.mergedCount || 1,
        totalGames: merged.estimatedTotalGames,
      }
    : null;

  return {
    // 数据源管理
    sources,
    selectedSourceIds,
    setSelectedSourceIds,
    toggleSource,
    selectOnly,
    selectByLeague,
    sourceErrors,
    manifestError,
    currentMeta,
    // 当前合并后的数据
    data: merged,
    loading,
    error: Object.values(sourceErrors)[0] || null,
    available: !!merged && Array.isArray(merged.champions) && merged.champions.length > 0,
    // 查询工具
    lookup,
    isOP,
    winRateTier,
  };
};

export default useTournamentData;
